/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { FlightRule, UnitStatus, WeatherStatus } from 'src/graphql/generated/graphql.generated';
import {
  UnitShiftSummaryRecord,
  UnitShiftSummaryProps,
} from 'src/store/generated/unit-shift-summary-record.generated';
import {
  OutOfServiceEventSummaryRecord,
  OutOfServiceEventSummaryProps,
} from 'src/store/generated/out-of-service-event-summary-record.generated';
import {
  UnitPositionSummaryRecord,
  UnitPositionSummaryProps,
} from 'src/store/generated/unit-position-summary-record.generated';
import {
  IdListItemRecord,
  IdListItemProps,
} from 'src/store/generated/id-list-item-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface UnitSummaryProps {
  id: string;
  name: string;
  abbreviation: string;
  status: UnitStatus;
  weatherStatus: WeatherStatus;
  isAdministrative: boolean | null;
  isHidden: boolean | null;
  isThirdPartyUnit: boolean | null;
  etloInMinutes: number | null;
  pilotNotes: string | null;
  lastPilotUpdateTime: Date | null;
  lastPilotUpdateById: string | null;
  isExternallyManaged: boolean;
  maximumFlightRule: FlightRule;
  isOnDelay: boolean;
  plannedAvailableNotes: string | null;
  plannedAvailableTime: Date | null;
  isEnabled: boolean;
  isSpare: boolean;
  lastPilotUpdateName: string | null;
  baseTimezoneCd: string | null;
  shifts: List<UnitShiftSummaryProps>;
  vehicleSummaryId: string | null;
  outOfServiceEvents: List<OutOfServiceEventSummaryProps>;
  positions: List<UnitPositionSummaryProps>;
  isCrewAvailable: boolean;
  paipEventIds: List<IdListItemProps> | null;
  baseId: string;
  vehicleId: string | null;
  regionId: string;
  communicationCenterId: string | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
}

export class UnitSummaryRecord extends Record<UnitSummaryProps>({
  id: null as any,
  name: null as any,
  abbreviation: null as any,
  status: null as any,
  weatherStatus: null as any,
  isAdministrative: null as any,
  isHidden: null as any,
  isThirdPartyUnit: null as any,
  etloInMinutes: null as any,
  pilotNotes: null as any,
  lastPilotUpdateTime: null as any,
  lastPilotUpdateById: null as any,
  isExternallyManaged: null as any,
  maximumFlightRule: null as any,
  isOnDelay: null as any,
  plannedAvailableNotes: null as any,
  plannedAvailableTime: null as any,
  isEnabled: null as any,
  isSpare: null as any,
  lastPilotUpdateName: null as any,
  baseTimezoneCd: null as any,
  shifts: null as any,
  vehicleSummaryId: null as any,
  outOfServiceEvents: null as any,
  positions: null as any,
  isCrewAvailable: null as any,
  paipEventIds: null as any,
  baseId: null as any,
  vehicleId: null as any,
  regionId: null as any,
  communicationCenterId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    abbreviation: string;
    status: UnitStatus;
    weatherStatus: WeatherStatus;
    isAdministrative: boolean | null;
    isHidden: boolean | null;
    isThirdPartyUnit: boolean | null;
    etloInMinutes: number | null;
    pilotNotes: string | null;
    lastPilotUpdateTime: string | number | Date | null;
    lastPilotUpdateById: string | null;
    isExternallyManaged: boolean;
    maximumFlightRule: FlightRule;
    isOnDelay: boolean;
    plannedAvailableNotes: string | null;
    plannedAvailableTime: string | number | Date | null;
    isEnabled: boolean;
    isSpare: boolean;
    lastPilotUpdateName: string | null;
    baseTimezoneCd: string | null;
    shifts: any;
    vehicleSummaryId: string | null;
    outOfServiceEvents: any;
    positions: any;
    isCrewAvailable: boolean;
    paipEventIds: any;
    baseId: string;
    vehicleId: string | null;
    regionId: string;
    communicationCenterId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
  }): UnitSummaryRecord {
    if (json == null) {
      throw new Error('UnitSummaryRecord.fromJS: parameter cannot be null');
    }
    return new UnitSummaryRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'UnitSummaryJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'UnitSummaryJson.name'),
      abbreviation: getFromOrThrow(json, (j) => j.abbreviation, 'UnitSummaryJson.abbreviation'),
      status: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'status', UnitStatus.Available),
        'UnitSummaryJson.status',
      ),
      weatherStatus: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'weatherStatus', WeatherStatus.Unspecified),
        'UnitSummaryJson.weatherStatus',
      ),
      isAdministrative: json.isAdministrative,
      isHidden: json.isHidden,
      isThirdPartyUnit: json.isThirdPartyUnit,
      etloInMinutes: json.etloInMinutes,
      pilotNotes: json.pilotNotes,
      lastPilotUpdateTime:
        json.lastPilotUpdateTime != null ? new Date(json.lastPilotUpdateTime) : null,
      lastPilotUpdateById: json.lastPilotUpdateById,
      isExternallyManaged: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isExternallyManaged', false),
        'UnitSummaryJson.isExternallyManaged',
      ),
      maximumFlightRule: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'maximumFlightRule', FlightRule.Ifr),
        'UnitSummaryJson.maximumFlightRule',
      ),
      isOnDelay: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isOnDelay', false),
        'UnitSummaryJson.isOnDelay',
      ),
      plannedAvailableNotes: json.plannedAvailableNotes,
      plannedAvailableTime:
        json.plannedAvailableTime != null ? new Date(json.plannedAvailableTime) : null,
      isEnabled: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isEnabled', false),
        'UnitSummaryJson.isEnabled',
      ),
      isSpare: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isSpare', false),
        'UnitSummaryJson.isSpare',
      ),
      lastPilotUpdateName: json.lastPilotUpdateName,
      baseTimezoneCd: json.baseTimezoneCd,
      shifts: List<UnitShiftSummaryRecord>(
        getFromOrThrow(json, (j) => j.shifts, 'UnitSummaryJson.shifts').map(
          (o: any) => new UnitShiftSummaryRecord(o),
        ),
      ),
      vehicleSummaryId: json.vehicleSummaryId,
      outOfServiceEvents: List<OutOfServiceEventSummaryRecord>(
        getFromOrThrow(json, (j) => j.outOfServiceEvents, 'UnitSummaryJson.outOfServiceEvents').map(
          (o: any) => new OutOfServiceEventSummaryRecord(o),
        ),
      ),
      positions: List<UnitPositionSummaryRecord>(
        getFromOrThrow(json, (j) => j.positions, 'UnitSummaryJson.positions').map(
          (o: any) => new UnitPositionSummaryRecord(o),
        ),
      ),
      isCrewAvailable: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isCrewAvailable', false),
        'UnitSummaryJson.isCrewAvailable',
      ),
      paipEventIds:
        json.paipEventIds != null
          ? List<IdListItemRecord>(json.paipEventIds.map((o: any) => new IdListItemRecord(o)))
          : null,
      baseId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'baseId', '00000000-0000-0000-0000-000000000000'),
        'UnitSummaryJson.baseId',
      ),
      vehicleId: json.vehicleId,
      regionId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'regionId', '00000000-0000-0000-0000-000000000000'),
        'UnitSummaryJson.regionId',
      ),
      communicationCenterId: json.communicationCenterId,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'UnitSummaryJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    abbreviation: string;
    status: UnitStatus;
    weatherStatus: WeatherStatus;
    isAdministrative: boolean | null;
    isHidden: boolean | null;
    isThirdPartyUnit: boolean | null;
    etloInMinutes: number | null;
    pilotNotes: string | null;
    lastPilotUpdateTime: string | number | Date | null;
    lastPilotUpdateById: string | null;
    isExternallyManaged: boolean;
    maximumFlightRule: FlightRule;
    isOnDelay: boolean;
    plannedAvailableNotes: string | null;
    plannedAvailableTime: string | number | Date | null;
    isEnabled: boolean;
    isSpare: boolean;
    lastPilotUpdateName: string | null;
    baseTimezoneCd: string | null;
    shifts: any;
    vehicleSummaryId: string | null;
    outOfServiceEvents: any;
    positions: any;
    isCrewAvailable: boolean;
    paipEventIds: any;
    baseId: string;
    vehicleId: string | null;
    regionId: string;
    communicationCenterId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
  }): [UnitSummaryRecord, Error[]] {
    if (json == null) {
      throw new Error('UnitSummaryRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new UnitSummaryRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'UnitSummaryJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'UnitSummaryJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      abbreviation: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.abbreviation, 'UnitSummaryJson.abbreviation'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'status', UnitStatus.Available),
            'UnitSummaryJson.status',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      weatherStatus: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'weatherStatus', WeatherStatus.Unspecified),
            'UnitSummaryJson.weatherStatus',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isAdministrative: tryOrDefault(
        () => json.isAdministrative,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isHidden: tryOrDefault(
        () => json.isHidden,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isThirdPartyUnit: tryOrDefault(
        () => json.isThirdPartyUnit,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      etloInMinutes: tryOrDefault(
        () => json.etloInMinutes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      pilotNotes: tryOrDefault(
        () => json.pilotNotes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastPilotUpdateTime: tryOrDefault(
        () => (json.lastPilotUpdateTime != null ? new Date(json.lastPilotUpdateTime) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastPilotUpdateById: tryOrDefault(
        () => json.lastPilotUpdateById,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isExternallyManaged: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isExternallyManaged', false),
            'UnitSummaryJson.isExternallyManaged',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      maximumFlightRule: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'maximumFlightRule', FlightRule.Ifr),
            'UnitSummaryJson.maximumFlightRule',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isOnDelay: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isOnDelay', false),
            'UnitSummaryJson.isOnDelay',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      plannedAvailableNotes: tryOrDefault(
        () => json.plannedAvailableNotes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      plannedAvailableTime: tryOrDefault(
        () => (json.plannedAvailableTime != null ? new Date(json.plannedAvailableTime) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isEnabled: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isEnabled', false),
            'UnitSummaryJson.isEnabled',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isSpare: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isSpare', false),
            'UnitSummaryJson.isSpare',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastPilotUpdateName: tryOrDefault(
        () => json.lastPilotUpdateName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      baseTimezoneCd: tryOrDefault(
        () => json.baseTimezoneCd,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      shifts: tryOrDefault(
        () =>
          List<UnitShiftSummaryRecord>(
            getFromOrThrow(json, (j) => j.shifts, 'UnitSummaryJson.shifts').map(
              (o: any) => new UnitShiftSummaryRecord(o),
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleSummaryId: tryOrDefault(
        () => json.vehicleSummaryId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      outOfServiceEvents: tryOrDefault(
        () =>
          List<OutOfServiceEventSummaryRecord>(
            getFromOrThrow(
              json,
              (j) => j.outOfServiceEvents,
              'UnitSummaryJson.outOfServiceEvents',
            ).map((o: any) => new OutOfServiceEventSummaryRecord(o)),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      positions: tryOrDefault(
        () =>
          List<UnitPositionSummaryRecord>(
            getFromOrThrow(json, (j) => j.positions, 'UnitSummaryJson.positions').map(
              (o: any) => new UnitPositionSummaryRecord(o),
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isCrewAvailable: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isCrewAvailable', false),
            'UnitSummaryJson.isCrewAvailable',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      paipEventIds: tryOrDefault(
        () =>
          json.paipEventIds != null
            ? List<IdListItemRecord>(json.paipEventIds.map((o: any) => new IdListItemRecord(o)))
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      baseId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'baseId', '00000000-0000-0000-0000-000000000000'),
            'UnitSummaryJson.baseId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleId: tryOrDefault(
        () => json.vehicleId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      regionId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'regionId', '00000000-0000-0000-0000-000000000000'),
            'UnitSummaryJson.regionId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      communicationCenterId: tryOrDefault(
        () => json.communicationCenterId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'UnitSummaryJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
    });

    return [record, errors];
  }

  constructor(props: UnitSummaryProps) {
    super(props);
    if (props == null) {
      throw new Error('UnitSummaryRecord constructor parameter cannot be null');
    }
  }
}

export abstract class UnitSummaryViewModelBase implements UnitSummaryProps {
  protected readonly _props: UnitSummaryProps;
  private _json?: UnitSummaryProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get abbreviation(): string {
    return this._props.abbreviation;
  }
  get status(): UnitStatus {
    return this._props.status;
  }
  get weatherStatus(): WeatherStatus {
    return this._props.weatherStatus;
  }
  get isAdministrative(): boolean | null {
    return this._props.isAdministrative;
  }
  get isHidden(): boolean | null {
    return this._props.isHidden;
  }
  get isThirdPartyUnit(): boolean | null {
    return this._props.isThirdPartyUnit;
  }
  get etloInMinutes(): number | null {
    return this._props.etloInMinutes;
  }
  get pilotNotes(): string | null {
    return this._props.pilotNotes;
  }
  get lastPilotUpdateTime(): Date | null {
    return this._props.lastPilotUpdateTime;
  }
  get lastPilotUpdateById(): string | null {
    return this._props.lastPilotUpdateById;
  }
  get isExternallyManaged(): boolean {
    return this._props.isExternallyManaged;
  }
  get maximumFlightRule(): FlightRule {
    return this._props.maximumFlightRule;
  }
  get isOnDelay(): boolean {
    return this._props.isOnDelay;
  }
  get plannedAvailableNotes(): string | null {
    return this._props.plannedAvailableNotes;
  }
  get plannedAvailableTime(): Date | null {
    return this._props.plannedAvailableTime;
  }
  get isEnabled(): boolean {
    return this._props.isEnabled;
  }
  get isSpare(): boolean {
    return this._props.isSpare;
  }
  get lastPilotUpdateName(): string | null {
    return this._props.lastPilotUpdateName;
  }
  get baseTimezoneCd(): string | null {
    return this._props.baseTimezoneCd;
  }
  get shifts(): List<UnitShiftSummaryProps> {
    return this._props.shifts;
  }
  get vehicleSummaryId(): string | null {
    return this._props.vehicleSummaryId;
  }
  get outOfServiceEvents(): List<OutOfServiceEventSummaryProps> {
    return this._props.outOfServiceEvents;
  }
  get positions(): List<UnitPositionSummaryProps> {
    return this._props.positions;
  }
  get isCrewAvailable(): boolean {
    return this._props.isCrewAvailable;
  }
  get paipEventIds(): List<IdListItemProps> | null {
    return this._props.paipEventIds;
  }
  get baseId(): string {
    return this._props.baseId;
  }
  get vehicleId(): string | null {
    return this._props.vehicleId;
  }
  get regionId(): string {
    return this._props.regionId;
  }
  get communicationCenterId(): string | null {
    return this._props.communicationCenterId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }

  public get isAvailable() {
    return this._props.status === UnitStatus.Available;
  }
  public get isCommitted() {
    return this._props.status === UnitStatus.Committed;
  }
  public get isOnStandby() {
    return this._props.status === UnitStatus.OnStandby;
  }
  public get isOutOfService() {
    return this._props.status === UnitStatus.OutOfService;
  }
  public get isPartiallyAvailable() {
    return this._props.status === UnitStatus.PartiallyAvailable;
  }
  public get isGreen() {
    return this._props.weatherStatus === WeatherStatus.Green;
  }
  public get isRed() {
    return this._props.weatherStatus === WeatherStatus.Red;
  }
  public get isUnspecified() {
    return this._props.weatherStatus === WeatherStatus.Unspecified;
  }
  public get isYellow() {
    return this._props.weatherStatus === WeatherStatus.Yellow;
  }
  public get isIfr() {
    return this._props.maximumFlightRule === FlightRule.Ifr;
  }
  public get isNone() {
    return this._props.maximumFlightRule === FlightRule.None;
  }
  public get isVfr() {
    return this._props.maximumFlightRule === FlightRule.Vfr;
  }

  public isAnyStatus(...unitStatus: UnitStatus[]) {
    return unitStatus.includes(this._props.status);
  }
  public isAnyWeatherStatus(...weatherStatus: WeatherStatus[]) {
    return weatherStatus.includes(this._props.weatherStatus);
  }
  public isAnyMaximumFlightRule(...flightRule: FlightRule[]) {
    return flightRule.includes(this._props.maximumFlightRule);
  }

  constructor(props: UnitSummaryProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        abbreviation: this._props.abbreviation,
        status: this._props.status,
        weatherStatus: this._props.weatherStatus,
        isAdministrative: this._props.isAdministrative,
        isHidden: this._props.isHidden,
        isThirdPartyUnit: this._props.isThirdPartyUnit,
        etloInMinutes: this._props.etloInMinutes,
        pilotNotes: this._props.pilotNotes,
        lastPilotUpdateTime: this._props.lastPilotUpdateTime,
        lastPilotUpdateById: this._props.lastPilotUpdateById,
        isExternallyManaged: this._props.isExternallyManaged,
        maximumFlightRule: this._props.maximumFlightRule,
        isOnDelay: this._props.isOnDelay,
        plannedAvailableNotes: this._props.plannedAvailableNotes,
        plannedAvailableTime: this._props.plannedAvailableTime,
        isEnabled: this._props.isEnabled,
        isSpare: this._props.isSpare,
        lastPilotUpdateName: this._props.lastPilotUpdateName,
        baseTimezoneCd: this._props.baseTimezoneCd,
        shifts: this._props.shifts,
        vehicleSummaryId: this._props.vehicleSummaryId,
        outOfServiceEvents: this._props.outOfServiceEvents,
        positions: this._props.positions,
        isCrewAvailable: this._props.isCrewAvailable,
        paipEventIds: this._props.paipEventIds,
        baseId: this._props.baseId,
        vehicleId: this._props.vehicleId,
        regionId: this._props.regionId,
        communicationCenterId: this._props.communicationCenterId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllUnitSummaryEntities = (state: AppState) =>
  state.entityStores.unitSummary.entities;

export const selectUnitSummaryById = (id: string) => (state: AppState) =>
  state.entityStores.unitSummary.entities.get(id);

export const selectUnitSummaryMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.unitSummary.meta.get(id);

export const selectUnitSummaryAndMetadataById = (id: string) =>
  createSelector(
    selectUnitSummaryById(id),
    selectUnitSummaryMetadataById(id),
    (unitSummary, meta) => ({ unitSummary, meta }),
  );

export function selectUnitSummaryEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllUnitSummaryEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, UnitSummaryRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectUnitSummaryEntitiesByName(name: string) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByAbbreviation(abbreviation: string) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.abbreviation === abbreviation).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByStatus(status: UnitStatus) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.status === status).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByWeatherStatus(weatherStatus: WeatherStatus) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.weatherStatus === weatherStatus).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByIsAdministrative(isAdministrative: boolean) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.isAdministrative === isAdministrative).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByIsHidden(isHidden: boolean) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.isHidden === isHidden).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByIsThirdPartyUnit(isThirdPartyUnit: boolean) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.isThirdPartyUnit === isThirdPartyUnit).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByEtloInMinutes(etloInMinutes: number) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.etloInMinutes === etloInMinutes).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByPilotNotes(pilotNotes: string) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.pilotNotes === pilotNotes).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByLastPilotUpdateTime(lastPilotUpdateTime: Date) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.lastPilotUpdateTime === lastPilotUpdateTime).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByLastPilotUpdateById(lastPilotUpdateById: string) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.lastPilotUpdateById === lastPilotUpdateById).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByIsExternallyManaged(isExternallyManaged: boolean) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.isExternallyManaged === isExternallyManaged).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByMaximumFlightRule(maximumFlightRule: FlightRule) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.maximumFlightRule === maximumFlightRule).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByIsOnDelay(isOnDelay: boolean) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.isOnDelay === isOnDelay).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByPlannedAvailableNotes(plannedAvailableNotes: string) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.plannedAvailableNotes === plannedAvailableNotes).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByPlannedAvailableTime(plannedAvailableTime: Date) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.plannedAvailableTime === plannedAvailableTime).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByIsEnabled(isEnabled: boolean) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.isEnabled === isEnabled).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByIsSpare(isSpare: boolean) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.isSpare === isSpare).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByLastPilotUpdateName(lastPilotUpdateName: string) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.lastPilotUpdateName === lastPilotUpdateName).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByBaseTimezoneCd(baseTimezoneCd: string) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.baseTimezoneCd === baseTimezoneCd).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByVehicleSummaryId(vehicleSummaryId: string) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.vehicleSummaryId === vehicleSummaryId).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByIsCrewAvailable(isCrewAvailable: boolean) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.isCrewAvailable === isCrewAvailable).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByBaseId(baseId: string) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.baseId === baseId).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByVehicleId(vehicleId: string) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.vehicleId === vehicleId).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByRegionId(regionId: string) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.regionId === regionId).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByCommunicationCenterId(communicationCenterId: string) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.communicationCenterId === communicationCenterId).valueSeq(),
  );
}

export function selectUnitSummaryEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllUnitSummaryEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
